import { CARE_ORDER_BLOTTER_PREFIX } from '@src/containers/Blotters/CareOrders/types';
import { cloneDeep } from 'lodash-es';
import type { AppConfigState } from '../AppConfigProvider.types';

type BlotterStateEntry = AppConfigState['blotters3'][string];
type CareOrdersPanelParams = {
  type?: string;
  blotterTablesState?: Record<string, BlotterStateEntry>;
};
type CareOrdersPanel = {
  id?: string;
  contentComponent?: string;
  params?: CareOrdersPanelParams;
};
export const CARE_ORDERS_BLOTTER_STATE_PANEL_MIGRATION_VERSION = 1;

function shouldRunCareOrdersBlotterStatePanelMigration(config: {
  careOrdersBlotterStatePanelMigrationVersion?: number;
}): boolean {
  const v = config.careOrdersBlotterStatePanelMigrationVersion;
  return v == null || v < CARE_ORDERS_BLOTTER_STATE_PANEL_MIGRATION_VERSION;
}

/**
 * Copies legacy care orders blotter state from app-wide `blotters3` into
 * care orders panel `params.blotterTablesState`, keyed by full blotter id.
 *
 * Legacy care orders blotter ids are panel-scoped:
 * - `${CARE_ORDER_BLOTTER_PREFIX}/${panelId}`
 *
 * This migration is intentionally copy-only: legacy `blotters3` remains untouched.
 */
export function migrateCareOrdersBlotterStateToPanelParams(initialConfig: unknown) {
  const config = initialConfig as AppConfigState & { careOrdersBlotterStatePanelMigrationVersion?: number };
  if (!shouldRunCareOrdersBlotterStatePanelMigration(config)) {
    return;
  }

  const legacyBlotterState = config.blotters3;
  if (legacyBlotterState == null) {
    config.careOrdersBlotterStatePanelMigrationVersion = CARE_ORDERS_BLOTTER_STATE_PANEL_MIGRATION_VERSION;
    return;
  }

  for (const spaceConfig of config.spacesConfigs?.trading?.layouts ?? []) {
    const panels = spaceConfig?.dockViewLayout?.panels as Record<string, CareOrdersPanel | undefined> | undefined;
    if (panels == null) {
      continue;
    }

    for (const [panelKey, panel] of Object.entries(panels)) {
      if (panel == null) {
        continue;
      }

      const panelParams = panel.params;
      const panelType = String(panelParams?.type ?? panel.contentComponent ?? '');
      if (panelType !== 'careOrders') {
        continue;
      }

      const panelIdCandidates = Array.from(new Set([panelKey, String(panel.id ?? '')]).values()).filter(Boolean);
      const blotterIDs = getLegacyCareOrdersBlotterIDsForPanel({
        panelIdCandidates,
        legacyBlotterState,
      });
      if (blotterIDs.length === 0) {
        continue;
      }

      const currentPanelStateMap = panelParams?.blotterTablesState ?? {};
      const nextPanelStateMap = { ...currentPanelStateMap };
      let changed = false;

      for (const blotterID of blotterIDs) {
        if (nextPanelStateMap[blotterID] != null) {
          continue;
        }

        nextPanelStateMap[blotterID] = cloneDeep(legacyBlotterState[blotterID]);
        changed = true;
      }

      if (changed) {
        const nextPanelParams: CareOrdersPanelParams = {
          ...(panelParams ?? {}),
          type: 'careOrders',
          blotterTablesState: nextPanelStateMap,
        };
        panel.params = nextPanelParams;
      }
    }
  }

  config.careOrdersBlotterStatePanelMigrationVersion = CARE_ORDERS_BLOTTER_STATE_PANEL_MIGRATION_VERSION;
}

function getLegacyCareOrdersBlotterIDsForPanel({
  panelIdCandidates,
  legacyBlotterState,
}: {
  panelIdCandidates: string[];
  legacyBlotterState: AppConfigState['blotters3'];
}): string[] {
  const blotterIDs = new Set<string>();

  for (const panelId of panelIdCandidates) {
    const singlePanelKey = `${CARE_ORDER_BLOTTER_PREFIX}/${panelId}`;
    if (legacyBlotterState[singlePanelKey] != null) {
      blotterIDs.add(singlePanelKey);
    }
  }

  return [...blotterIDs];
}
